import React, { useState } from 'react'
import { cn } from '../../lib/utils'
import ActivityLog from './ActivityLog'
import JobSearches from './JobSearches'
import { useCandidate } from './CandidateProvider'
import JobAlerts from './JobAlerts'
import { useQuery } from '@tanstack/react-query'
import { apiGet } from '~/util/api'

export const useCandidateActivity = () => {
  const { candidate: { id: candidateId } = {} } = useCandidate()

  const queryPath = `/coach/dashboard_candidates/${candidateId}/candidate_activity.json`

  const queryFn = () => apiGet(queryPath).then((res) => res.data)
  const { data, error, isFetching } = useQuery({
    queryKey: ['coaching_summary', candidateId],
    queryFn,
    enabled: !!candidateId,
  })

  if (error) {
    console.error('Error retrieving candidate activity:', error)
    return null
  }

  const {
    candidateAchievements = [],
    candidateActivities = [],
    candidateJobSearches = [],
    candidateJobAlerts,
  } = data || {}
  return {
    candidateAchievements,
    candidateActivities,
    candidateJobSearches,
    candidateJobAlerts,
    isFetching,
  }
}

const CandidateActivitySection = ({ className }) => {
  const [selectedTab, setSelectedTab] = useState('Platform Progress')

  const tabs = [
    'Platform Progress',
    'Activity Log',
    'Job Searches',
    'Job Alerts',
  ]

  return (
    <div
      className={cn(
        'my-8 flex flex-col justify-between divide-y divide-[#B9B9B9] md:mt-0',
        className,
      )}
    >
      <Page
        tabs={tabs}
        selectedTab={selectedTab}
        setSelectedTab={setSelectedTab}
      />
    </div>
  )
}

function chunkArray(array, chunkSize) {
  const chunks = []
  for (let i = 0; i < array.length; i += chunkSize) {
    chunks.push(array.slice(i, i + chunkSize))
  }
  return chunks
}

const PlatformProgress = () => {
  const { candidateAchievements } = useCandidateActivity()
  const sections = Object.entries(candidateAchievements)

  // Distribute sections into 3 roughly equal columns
  const columnChunks = chunkArray(sections, Math.ceil(sections.length / 3))

  return (
    <div className="grid grid-cols-1 gap-8 md:grid-cols-3">
      {columnChunks.map((column, colIndex) => (
        <div key={`columnChunk-${colIndex}`} className="flex flex-col gap-6">
          {column.map(([sectionTitle, items]) => (
            <div key={sectionTitle}>
              <h2 className="mb-2 text-lg font-bold text-gray-800">
                {sectionTitle}
              </h2>
              <ul className="space-y-1">
                {items.map((item) => (
                  <li
                    key={`${sectionTitle}-${item.key}`}
                    className="flex items-start gap-2"
                  >
                    <input
                      type="checkbox"
                      checked={item.achieved}
                      readOnly
                      className="mt-1 accent-green-600"
                    />
                    <span
                      className={
                        item.achieved ? 'text-gray-900' : 'text-gray-500'
                      }
                    >
                      {item.name}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      ))}
    </div>
  )
}

const Page = ({ tabs, selectedTab, setSelectedTab }) => {
  return (
    <div className="flex w-full gap-8">
      <div className="flex w-max min-w-56 flex-col gap-4">
        {tabs.map((tab, idx) => {
          const selectedClass =
            selectedTab === tab ? 'text-[#fff] bg-intoo-blue-medium' : ''
          return (
            <div
              className={cn(
                `cursor-pointer rounded-[10px] px-5 py-[10px] text-[#333] checkbox-${tab}`,
                selectedClass,
              )}
              key={`tabs-list-${idx}`}
              onClick={() => setSelectedTab(tab)}
              onFocus={() => setSelectedTab(tab)}
              tabIndex={0}
              role="button"
            >
              <div className="grid grid-cols-5">
                <div className="col-span-4">{tab}</div>
              </div>
            </div>
          )
        })}
      </div>
      <div className="w-full">
        {selectedTab === 'Platform Progress' && <PlatformProgress />}
        {selectedTab === 'Activity Log' && <ActivityLog />}
        {selectedTab === 'Job Searches' && <JobSearches />}
        {selectedTab === 'Job Alerts' && <JobAlerts />}
      </div>
    </div>
  )
}

export default CandidateActivitySection
